import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Building2, Package } from 'lucide-react';
import {
  findBranch, findWarehouse,
  whPurchaseOrdersOf, findCatalogProduct, variantLabelOf,
  salesOf, stockRowsOf, emergencyRequestsOf
} from '../../data/store';
import { useSharedStore } from '../../hooks/useSharedStore';

const Field = ({ label, value, readOnly }) => (
  <label className="block">
    <span className="block text-[10px] tracking-[0.2em] font-bold uppercase text-slate-400 mb-1">{label}</span>
    <input
      type="text"
      value={value || ''}
      readOnly={readOnly}
      className={`w-full rounded bg-slate-50 border border-slate-200 px-3 py-2.5 text-sm font-bold text-slate-800 focus:outline-none focus:border-[var(--theme-primary)] focus:ring-2 focus:ring-[var(--theme-primary)]/20 transition-all ${readOnly ? 'bg-slate-100 text-slate-400 cursor-not-allowed outline-none focus:ring-0 focus:border-slate-200' : ''}`}
    />
  </label>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  readOnly: PropTypes.bool,
};

const Metric = ({ title, value }) => (
  <div className="flex flex-col">
    <h4 className="text-[10px] tracking-[0.2em] font-bold uppercase text-slate-400 mb-1">{title}</h4>
    <div className="text-2xl font-bold text-slate-800 tracking-tight leading-none">{value}</div>
  </div>
);

Metric.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export const MyBranchTab = ({ branchId, org }) => {
  const { version: dataVersion } = useSharedStore();
  const branch = useMemo(() => findBranch(branchId), [branchId, dataVersion]);

  const sourceWarehouses = useMemo(() => (
    (branch?.warehouseSourceIds || []).map((id) => findWarehouse(id)).filter(Boolean)
  ), [branch, dataVersion]);

  const sourcedProducts = useMemo(() => {
    const seen = new Map();
    sourceWarehouses.forEach((wh) => {
      whPurchaseOrdersOf(wh.id).forEach((po) => {
        const key = `${po.productId}:${po.variantId || ''}`;
        const existing = seen.get(key);
        const entry = existing || {
          key,
          productId: po.productId,
          variantId: po.variantId,
          warehouseNames: new Set(),
          qty: 0,
        };
        entry.warehouseNames.add(wh.name);
        entry.qty += Number(po.qty) || 0;
        seen.set(key, entry);
      });
    });
    return Array.from(seen.values()).map((e) => ({
      ...e,
      warehouseNames: Array.from(e.warehouseNames),
    }));
  }, [sourceWarehouses, dataVersion]);

  // Compute Metrics
  const metrics = useMemo(() => {
    const branchSales = salesOf(org.id).filter(s => s.branchId === branchId);
    const totalSalesValue = branchSales.reduce((acc, s) => acc + (s.total || 0), 0);

    const branchStock = stockRowsOf(org.id, branchId);
    const activeItems = branchStock.filter(r => r.status === 'active').reduce((acc, r) => acc + r.qty, 0);

    const branchEmergencies = emergencyRequestsOf(org.id).filter(r => r.requestingBranchId === branchId && r.status === 'open');
    const openEmergencies = branchEmergencies.length;

    return { totalSalesValue, activeItems, openEmergencies };
  }, [org.id, branchId, dataVersion]);

  if (!branch) {
    return (
      <div className="py-24 text-center">
        <p className="text-sm font-semibold text-slate-500">Branch not found.</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto w-full p-4 sm:p-6 lg:p-8 pb-12">
      
      {/* Unified Master Card */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm flex flex-col w-full overflow-hidden">

        {/* Section 1: Header & Metrics */}
        <div className="p-8 lg:p-10 pb-8 border-b border-slate-200 flex flex-col lg:flex-row lg:items-center justify-between gap-10">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                {branch.isMain ? 'Main Branch' : 'Branch Node'}
              </span>
              <span className="w-1 h-1 rounded-full bg-slate-300"></span>
              <span className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">
                {branch.status}
              </span>
            </div>
            <h2 className="text-4xl font-bold text-slate-900 tracking-tight uppercase">{branch.name}</h2>
          </div>

          <div className="flex flex-wrap items-center gap-12 lg:gap-16">
            <Metric title="Revenue" value={`₹${metrics.totalSalesValue.toLocaleString()}`} />
            <Metric title="Inventory" value={metrics.activeItems.toLocaleString()} />
            <Metric title="Emergencies" value={metrics.openEmergencies} />
          </div>
        </div>

        {/* Section 2: Branch Profile (Read-Only) */}
        <div className="p-8 lg:p-10 border-b border-slate-200 bg-white">
          <div className="flex items-center gap-4 mb-6">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-widest flex items-center gap-2">
              <Building2 size={16} className="text-slate-400" /> Branch Profile
            </h3>
          </div>

          <div className="flex flex-col gap-6 pointer-events-none">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Field label="Branch Name" value={branch.name} readOnly={true} />
              <Field label="Phone Number" value={branch.phone} readOnly={true} />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Field label="State" value={branch.state} readOnly={true} />
              <Field label="District" value={branch.district} readOnly={true} />
              <Field label="Zipcode" value={branch.zipcode} readOnly={true} />
            </div>
            <Field label="Full Address" value={branch.address} readOnly={true} />
          </div>
        </div>

        {/* Section 3: Incoming Products Feed */}
        <div className="p-0">
          <div className="p-8 lg:p-10 pb-6 bg-white border-b border-slate-100">
            <h3 className="text-xs font-bold text-slate-800 uppercase tracking-widest flex items-center gap-2">
              <Package size={16} className="text-slate-400" /> Incoming Products Feed
            </h3>
            <p className="text-[10px] font-bold text-slate-400 mt-2 ml-6 uppercase tracking-widest">Stock arriving from parent origin warehouses.</p>
          </div>

          {sourcedProducts.length === 0 ? (
            <div className="p-12 pb-16 flex flex-col items-center justify-center text-center">
              <Package size={32} className="text-slate-200 mb-4" />
              <h4 className="text-[10px] font-bold uppercase tracking-widest text-slate-400">No incoming stock</h4>
            </div>
          ) : (
            <div className="w-full pb-8">
              <table className="w-full text-left table-fixed">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                    <th className="py-3 px-8 lg:px-10 w-[40%]">Product Name</th>
                    <th className="py-3 px-6 w-[20%]">Variant</th>
                    <th className="py-3 px-6 w-[20%]">Origin</th>
                    <th className="py-3 px-8 lg:px-10 text-right w-[20%]">Received Qty</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {sourcedProducts.map((row) => {
                    const product = findCatalogProduct(row.productId);
                    const varL = variantLabelOf(product, row.variantId);
                    return (
                      <tr key={row.key} className="hover:bg-slate-50 transition-colors">
                        <td className="py-4 px-8 lg:px-10 text-sm font-bold text-slate-800">{product?.name || row.productId}</td>
                        <td className="py-4 px-6 text-xs font-bold text-slate-500">{varL}</td>
                        <td className="py-4 px-6 text-xs font-bold text-slate-500">
                          {row.warehouseNames.join(', ')}
                        </td>
                        <td className="py-4 px-8 lg:px-10 text-sm font-bold text-slate-800 text-right">{row.qty}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

MyBranchTab.propTypes = {
  branchId: PropTypes.string.isRequired,
  org: PropTypes.object.isRequired,
};
